import React from 'react';
import {useTranslations} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import Container from '@/container';
import {withMarginBottomXl, withMarginBottomLg, withMarginBottomXs} from '@/hocs/withSpacing';
import {SectionPadding} from '@/section';
import {H2, H4, P, PNoMargin} from '@/typography';
import {withBgNeutral100, withNeutral900, withPurple600} from '@/hocs/withColors';
import {withHeadingEyebrow} from '@/hocs/withFontType';
import {BreakVisibleInDesktop} from '@/breaks';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import Link from '@/links';
import styles from './styles.module.scss';

const Neutral100Section = withBgNeutral100(SectionPadding) as React.ComponentType<{
  id?: string;
  children?: React.ReactNode;
}>;
const H2Neutral900 = withMarginBottomLg(withNeutral900(H2));
const RowMargined = withMarginBottomXl(Row);
const EyebrowPurple600 = withPurple600(withHeadingEyebrow(P as never));
const PMarginBottomXs = withMarginBottomXs(P);
const FluidGrid = Grid as React.ComponentType<{
  fluid?: boolean;
  className?: string;
  tagName?: string;
  children?: React.ReactNode;
}>;

interface CreditBundle {
  credits: string;
  perCredit: string;
  totalPrice: string;
}

interface FeatureCardItem {
  title: string;
  text: string;
}

const AiAgentAddon = () => {
  const t = useTranslations('pricing.aiAgentAddon');
  const featureCards = t.raw('featureCards') as FeatureCardItem[];
  const creditBundles = t.raw('creditBundles') as CreditBundle[];
  const creditNotes = t.raw('creditNotes') as string[];

  return (
    <Neutral100Section id="workable-agent-credits">
      <Container>
        <FluidGrid fluid>
          <Row center="xs">
            <Col xs={12}>
              <EyebrowPurple600>{t('eyebrow')}</EyebrowPurple600>
              <H2Neutral900>
                {t.rich('heading', {
                  br: () => <BreakVisibleInDesktop />
                })}
              </H2Neutral900>
            </Col>
          </Row>
          <Row center="xs">
            <Col xs={12} lg={9}>
              <P className={styles['section-intro']}>{t('intro')}</P>
            </Col>
          </Row>
          <Row center="xs">
            <Col xs={12} lg={9}>
              <div className={styles['feature-cards']}>
                {featureCards.map((card, index) => (
                  <div key={`feature-card-${index}`} className={styles['feature-card']}>
                    <p className={styles['feature-card__title']}>{card.title}</p>
                    <p className={styles['feature-card__text']}>{card.text}</p>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
          <Row center="xs">
            <Col xs={12}>
              <P className={styles['credit-intro']}>{t('creditIntro')}</P>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <div className={styles['credit-bundles']}>
                <GridLayout Col={4} lgCol={2}>
                  {creditBundles.map((bundle, index) => (
                    <GridItem key={`credit-bundle-${index}`}>
                      <div className={styles['credit-card']}>
                        <P className={styles['credit-card__credits']}>{bundle.credits}</P>
                        <P className={styles['credit-card__price']}>
                          <span className={styles['credit-card__price-currency']}>
                            {t('creditCurrency')}
                          </span>
                          <span className={styles['credit-card__price-amount']}>
                            {bundle.perCredit}
                          </span>
                          <span className={styles['credit-card__price-unit']}>
                            {t('creditUnit')}
                          </span>
                        </P>
                        <P className={styles['credit-card__total']}>
                          <span className={styles['credit-card__total-price']}>
                            {bundle.totalPrice}
                          </span>{' '}
                          {t('creditTotal')}
                        </P>
                      </div>
                    </GridItem>
                  ))}
                  <GridItem>
                    <div className={styles['contact-card']}>
                      <H4 className={styles['contact-card__title']}>{t('contactBox.title')}</H4>
                      <P className={styles['contact-card__description']}>
                        {t('contactBox.description')}
                      </P>
                      <Link href={t('contactBox.href')} variant="cta-default-xs">
                        {t('contactBox.cta')}
                      </Link>
                    </div>
                  </GridItem>
                </GridLayout>
              </div>
            </Col>
          </Row>
          <RowMargined center="xs">
            <Col xs={12}>
              <div className={styles['credit-calc']}>
                <div>
                  <PMarginBottomXs className={styles['credit-calc__title']}>
                    {t('creditCalc.title')}
                  </PMarginBottomXs>
                  <PNoMargin className={styles['credit-calc__example']}>
                    {t('creditCalc.example')}
                  </PNoMargin>
                </div>
                <Link href={t('creditCalc.ctaHref')} variant="primary-sm">
                  {t('creditCalc.cta')}
                </Link>
              </div>
            </Col>
          </RowMargined>
          <Row center="xs">
            <Col xs={12}>
              <PNoMargin className={styles['credit-notes']}>
                {creditNotes.map((note, index) => (
                  <React.Fragment key={`credit-note-${index}`}>
                    {index > 0 && (
                      <span className={styles['credit-notes__separator']} aria-hidden="true" />
                    )}
                    {note}
                  </React.Fragment>
                ))}
              </PNoMargin>
            </Col>
          </Row>
        </FluidGrid>
      </Container>
    </Neutral100Section>
  );
};

export default AiAgentAddon;
